import { useState } from "react";
import { createFileRoute, Outlet, Link, useRouter, useLocation } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery } from "@tanstack/react-query";
import { getMyAccess } from "@/lib/sick-notes.functions";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { AdminSearch } from "@/components/admin/AdminSearch";
import {
  LayoutDashboard,
  CalendarDays,
  CalendarPlus,
  MessagesSquare,
  Users,
  ClipboardList,
  LogOut,
  Loader2,
  ShoppingBag,
  Receipt,
  CreditCard,
  BarChart3,
  FileText,
  PieChart,
  Settings,
  Shield,
  History,
  Menu,
  ExternalLink,
  NotebookPen,
  Stethoscope,
  FileSignature,
  Send,
  Dumbbell,
  Mail,
  Wallet,
} from "lucide-react";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/_authenticated/admin")({
  component: AdminLayout,
});

const NAV_GROUPS = [
  {
    title: "Practice",
    items: [
      { to: "/admin", label: "Dashboard", icon: LayoutDashboard, exact: true },
      { to: "/admin/bookings", label: "Bookings", icon: CalendarDays, exact: false },
      { to: "/admin/schedule", label: "Schedule & blocks", icon: CalendarPlus, exact: false },
      { to: "/admin/patients", label: "Patients", icon: Users, exact: false },
      { to: "/admin/assessments", label: "Assessments", icon: ClipboardList, exact: false },
      { to: "/admin/chats", label: "Website chats", icon: MessagesSquare, exact: false },
      { to: "/admin/exercise-programmes", label: "Exercise programmes", icon: Dumbbell, exact: false },
    ],
  },
  {
    title: "Sick Notes",
    items: [
      { to: "/admin/sick-notes/new", label: "Create Sick Note", icon: NotebookPen, exact: false },
      { to: "/admin/sick-notes", label: "Sick Note History", icon: FileText, exact: true },
      { to: "/admin/sick-notes/practitioner", label: "Practitioner profile", icon: Stethoscope, exact: false },
    ],
  },
  {
    title: "Medical Reports",
    items: [
      { to: "/admin/medical-reports/new", label: "Create Medical Report", icon: FileSignature, exact: false },
      { to: "/admin/medical-reports", label: "Medical Report History", icon: FileText, exact: true },
    ],
  },
  {
    title: "Referral Letters",
    items: [
      { to: "/admin/referral-letters/new", label: "Create Referral Letter", icon: Send, exact: false },
      { to: "/admin/referral-letters", label: "Referral Letter History", icon: FileText, exact: true },
    ],
  },
  {
    title: "Letters",
    items: [
      { to: "/admin/letters/new", label: "Create Letter", icon: Mail, exact: false },
      { to: "/admin/letters", label: "Letter History", icon: FileText, exact: true },
    ],
  },
  {
    title: "Employment Contracts",
    items: [
      { to: "/admin/contracts/new", label: "Create Contract", icon: FileSignature, exact: false },
      { to: "/admin/contracts", label: "Contract History", icon: FileText, exact: true },
      { to: "/admin/contracts/templates", label: "Contract Templates", icon: NotebookPen, exact: false },
    ],
  },
  {
    title: "Shop",
    items: [
      { to: "/admin/payroll", label: "Payroll", icon: Wallet, exact: false },
      { to: "/admin/products", label: "Products", icon: ShoppingBag, exact: false },
      { to: "/admin/orders", label: "Orders", icon: Receipt, exact: false },
      { to: "/admin/payments", label: "Payments", icon: CreditCard, exact: false },
      { to: "/admin/invoices", label: "Invoices", icon: FileText, exact: false },
    ],
  },
  {
    title: "Insights",
    items: [
      { to: "/admin/reports", label: "Reports", icon: PieChart, exact: false },
      { to: "/admin/analytics", label: "Analytics", icon: BarChart3, exact: false },
      { to: "/admin/activity", label: "Activity log", icon: History, exact: false },
    ],
  },
  {
    title: "Administration",
    items: [
      { to: "/admin/settings", label: "Settings", icon: Settings, exact: false },
      { to: "/admin/users", label: "Users", icon: Shield, exact: false },
    ],
  },
] as const;

function pageTitle(pathname: string): string {
  for (const g of NAV_GROUPS) {
    for (const i of g.items) {
      if (i.exact ? pathname === i.to : pathname.startsWith(i.to)) return i.label;
    }
  }
  return "Admin";
}

function AdminLayout() {
  const router = useRouter();
  const location = useLocation();
  const check = useServerFn(getMyAccess);
  const [menuOpen, setMenuOpen] = useState(false);
  const { data, isPending, isError } = useQuery({
    queryKey: ["sickNoteAccess"],
    queryFn: () => check(),
    staleTime: 60_000,
  });

  async function signOut() {
    await supabase.auth.signOut();
    router.navigate({ to: "/auth", replace: true });
  }

  if (isPending) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }
  if (isError || !data || (!data.admin && data.perms.length === 0)) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background px-4">
        <div className="max-w-md space-y-4 text-center">
          <h1 className="text-2xl font-semibold">Access denied</h1>
          <p className="text-sm text-muted-foreground">This account isn't authorised for the admin dashboard.</p>
          <Button onClick={signOut} variant="outline">
            <LogOut className="mr-2 h-4 w-4" />
            Sign out
          </Button>
        </div>
      </div>
    );
  }

  const staffOnly = !data.admin;
  const nav = <SidebarNav staffOnly={staffOnly} pathname={location.pathname} onNavigate={() => setMenuOpen(false)} onSignOut={signOut} />;

  return (
    <div className="min-h-screen bg-muted/30">
      <div className="flex">
        <aside className="sticky top-0 hidden h-screen w-60 shrink-0 border-r bg-background lg:block">{nav}</aside>

        <div className="min-w-0 flex-1">
          <header className="sticky top-0 z-30 flex items-center gap-3 border-b bg-background/95 px-4 py-3 backdrop-blur">
            <Sheet open={menuOpen} onOpenChange={setMenuOpen}>
              <SheetTrigger asChild>
                <Button variant="outline" size="icon" className="lg:hidden" aria-label="Open menu">
                  <Menu className="h-4 w-4" />
                </Button>
              </SheetTrigger>
              <SheetContent side="left" className="w-64 p-0">
                {nav}
              </SheetContent>
            </Sheet>

            <h1 className="shrink-0 text-base font-semibold tracking-tight sm:text-lg">
              {pageTitle(location.pathname)}
            </h1>

            <div className="ml-auto flex min-w-0 flex-1 items-center justify-end gap-2">
              <div className="hidden min-w-0 flex-1 justify-end sm:flex">
                <AdminSearch />
              </div>
              <Button asChild variant="ghost" size="sm" className="hidden sm:inline-flex">
                <a href="/" target="_blank" rel="noreferrer">
                  <ExternalLink className="mr-2 h-4 w-4" />
                  View site
                </a>
              </Button>
            </div>
          </header>

          <div className="px-4 pb-3 pt-3 sm:hidden">
            <AdminSearch />
          </div>

          <main className="min-w-0 p-4 sm:p-6">
            {staffOnly && !location.pathname.startsWith("/admin/sick-notes") && !location.pathname.startsWith("/admin/medical-reports") && !location.pathname.startsWith("/admin/referral-letters") && !location.pathname.startsWith("/admin/letters") ? (
              <p className="text-sm text-muted-foreground">Your account only has access to authorised clinical documents.</p>
            ) : (
              <Outlet />
            )}
          </main>
        </div>
      </div>
    </div>
  );
}

function SidebarNav({
  staffOnly,
  pathname,
  onNavigate,
  onSignOut,
}: {
  staffOnly?: boolean;
  pathname: string;
  onNavigate: () => void;
  onSignOut: () => void;
}) {
  return (
    <div className="flex h-full flex-col overflow-y-auto p-4">
      <div className="mb-4 px-2">
        <p className="text-xs uppercase tracking-widest text-muted-foreground">N S Physiotherapy</p>
        <p className="mt-1 text-sm font-semibold">Admin Dashboard</p>
      </div>

      <nav className="flex-1 space-y-5">
        {NAV_GROUPS.filter((g) => !staffOnly || g.title === "Sick Notes" || g.title === "Medical Reports" || g.title === "Referral Letters" || g.title === "Letters").map((g) => (
          <div key={g.title}>
            <p className="px-3 pb-1 text-[11px] uppercase tracking-widest text-muted-foreground">{g.title}</p>
            <div className="space-y-1">
              {g.items.map((n) => {
                const active = n.exact ? pathname === n.to : pathname.startsWith(n.to);
                const Icon = n.icon;
                return (
                  <Link
                    key={n.to}
                    to={n.to}
                    onClick={onNavigate}
                    className={cn(
                      "flex items-center gap-2 rounded-lg px-3 py-2 text-sm transition-colors",
                      active ? "bg-primary text-primary-foreground" : "text-foreground hover:bg-accent",
                    )}
                  >
                    <Icon className="h-4 w-4" />
                    <span>{n.label}</span>
                  </Link>
                );
              })}
            </div>
          </div>
        ))}
      </nav>

      <div className="mt-4 border-t pt-4">
        <Button variant="ghost" size="sm" className="w-full justify-start" onClick={onSignOut}>
          <LogOut className="mr-2 h-4 w-4" />
          Sign out
        </Button>
      </div>
    </div>
  );
}
